"use client";

/**
 * Files browser UI state.
 *
 * Single-instance Zustand store for everything the files surface
 * needs to coordinate across components: dialog open states,
 * multi-select state, tree expansion. Server data (the actual list
 * of files + folders) lives in React Query — this store is purely
 * UI scaffolding.
 *
 * Why one store, not per-mount React state:
 *   - The grid (FinderTile click), the tree (FolderTreeNode), the
 *     context menu, the keyboard, and the toolbar all read + write
 *     the same "what's selected" / "what dialog is open" facts.
 *   - Hoisting them into a single store removes the window-event
 *     bridges + ad-hoc prop drilling we had grown to coordinate
 *     across these components.
 *
 * Why not in computerStore: computerStore is task-scoped and per-tab
 * (open/active-tab/selection). This is the file-browser surface's
 * UI — it's the same model whether mounted in /knowledge/files
 * (singleton page) or inside the Computer panel (per-task). One
 * canvas is active at a time in practice; we don't try to coordinate
 * two simultaneously.
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { PreviewTarget } from "./preview-target";
// One-directional store dependency: computerStore never imports this module
// at runtime (only the PreviewTarget TYPE from preview-target), so no cycle.
import {
  useComputerStore,
  KNOWLEDGE_BROWSER_KEY,
} from "@/stores/computerStore";

export interface RenameTarget {
  id: string;
  /** Generic label — for files this is the filename (with extension);
   *  for folders this is the folder name. The dialog component reuses
   *  the same field. */
  filename: string;
  /** Whether this is a folder. The submit handler routes to
   *  `renameFolder` instead of `renameFile` when true. */
  isFolder?: boolean;
}

export interface MoveTarget {
  id: string;
  currentFolderId: string | null;
}

export interface BulkMoveTarget {
  ids: string[];
  currentFolderId: string | null;
}

interface FilesUIState {
  /** New folder dialog. `null` = closed. The string is the parent
   *  folder UUID (or `null` for root). We use `parentId` as the
   *  "open" sentinel via a separate `isOpen` flag because parentId=null
   *  is a valid value for root-level creation. */
  newFolderOpen: boolean;
  newFolderParentId: string | null;

  renameTarget: RenameTarget | null;
  moveTarget: MoveTarget | null;
  bulkMoveTarget: BulkMoveTarget | null;

  /** The universal file preview overlay (any file, any folder). */
  previewTarget: PreviewTarget | null;

  /** Multi-select state. Cmd/Ctrl+click toggles a single id;
   *  Shift+click extends from `lastSelectedFileId`. */
  selectedFileIds: Set<string>;
  lastSelectedFileId: string | null;

  /** Tree-row expansion. Set of folder UUIDs that are open.
   *  Top-level "My files" / "Uploaded" / "Shared with me" sections
   *  are tracked separately because they're not real folders. */
  treeExpanded: Set<string>;
  workspaceTreeOpen: boolean;
  uploadedTreeOpen: boolean;
  sharedTreeOpen: boolean;
  thisTaskTreeOpen: boolean;

  // ── Actions ──────────────────────────────────────────────────────────

  openNewFolder: (parentId: string | null) => void;
  closeNewFolder: () => void;

  /** Open the universal preview. `opts.taskId` names the originating task
   *  surface: under split V1 an in-task click routes to the Computer pane's
   *  Preview tab instead of the overlay (the ONE routing decision — see the
   *  action body). Omit it, or pass KNOWLEDGE_BROWSER_KEY, for the overlay. */
  openPreview: (
    target: PreviewTarget,
    opts?: { taskId?: string | null }
  ) => void;
  closePreview: () => void;

  openRename: (target: RenameTarget) => void;
  closeRename: () => void;

  openMove: (target: MoveTarget) => void;
  closeMove: () => void;

  openBulkMove: (target: BulkMoveTarget) => void;
  closeBulkMove: () => void;

  toggleSelect: (fileId: string, additive: boolean) => void;
  rangeSelect: (toFileId: string, candidates: string[]) => void;
  clearSelection: () => void;

  toggleFolderExpand: (folderId: string) => void;
  setWorkspaceTreeOpen: (open: boolean) => void;
  setUploadedTreeOpen: (open: boolean) => void;
  setSharedTreeOpen: (open: boolean) => void;
  setThisTaskTreeOpen: (open: boolean) => void;
}

export const useFilesStore = create<FilesUIState>()(
  subscribeWithSelector((set) => ({
    newFolderOpen: false,
    newFolderParentId: null,
    renameTarget: null,
    moveTarget: null,
    bulkMoveTarget: null,
    previewTarget: null,

    selectedFileIds: new Set<string>(),
    lastSelectedFileId: null,

    treeExpanded: new Set<string>(),
    workspaceTreeOpen: false,
    uploadedTreeOpen: false,
    sharedTreeOpen: false,
    thisTaskTreeOpen: false,

    openNewFolder: (parentId) =>
      set({ newFolderOpen: true, newFolderParentId: parentId }),
    closeNewFolder: () => set({ newFolderOpen: false }),

    openPreview: (target, opts) => {
      const originTaskId = opts?.taskId ?? null;
      // An in-task click belongs to the Computer pane's own preview — a
      // Dialog layered over whatever the pane is already showing (Files,
      // most often), never a surface swap, so there's no "quick look would
      // cost you the chat" tradeoff to gate on viewport anymore: it opens
      // the same way whether the pane sits beside the chat (>= md) or full-
      // screen over it (< md, computerPaneClasses). The Knowledge browser
      // (sentinel key) has no Computer pane to open it in, so it always
      // keeps the universal overlay.
      if (originTaskId && originTaskId !== KNOWLEDGE_BROWSER_KEY) {
        useComputerStore.getState().openPreviewTarget(originTaskId, target);
        return;
      }
      set({ previewTarget: target });
    },
    closePreview: () => set({ previewTarget: null }),

    openRename: (target) => set({ renameTarget: target }),
    closeRename: () => set({ renameTarget: null }),

    openMove: (target) => set({ moveTarget: target }),
    closeMove: () => set({ moveTarget: null }),

    openBulkMove: (target) => set({ bulkMoveTarget: target }),
    closeBulkMove: () => set({ bulkMoveTarget: null }),

    toggleSelect: (fileId, additive) =>
      set((s) => {
        if (!additive) {
          // Plain toggle — single-select replace
          return {
            selectedFileIds: new Set([fileId]),
            lastSelectedFileId: fileId,
          };
        }
        const next = new Set(s.selectedFileIds);
        if (next.has(fileId)) next.delete(fileId);
        else next.add(fileId);
        return { selectedFileIds: next, lastSelectedFileId: fileId };
      }),

    rangeSelect: (toFileId, candidates) =>
      set((s) => {
        const anchor = s.lastSelectedFileId;
        if (!anchor || !candidates.includes(anchor)) {
          // No anchor or it was removed — fall back to single-select
          return {
            selectedFileIds: new Set([toFileId]),
            lastSelectedFileId: toFileId,
          };
        }
        const a = candidates.indexOf(anchor);
        const b = candidates.indexOf(toFileId);
        if (b < 0) return s;
        const [lo, hi] = a < b ? [a, b] : [b, a];
        return {
          selectedFileIds: new Set(candidates.slice(lo, hi + 1)),
          lastSelectedFileId: toFileId,
        };
      }),

    clearSelection: () =>
      set({ selectedFileIds: new Set(), lastSelectedFileId: null }),

    toggleFolderExpand: (folderId) =>
      set((s) => {
        const next = new Set(s.treeExpanded);
        if (next.has(folderId)) next.delete(folderId);
        else next.add(folderId);
        return { treeExpanded: next };
      }),

    setWorkspaceTreeOpen: (open) => set({ workspaceTreeOpen: open }),
    setUploadedTreeOpen: (open) => set({ uploadedTreeOpen: open }),
    setSharedTreeOpen: (open) => set({ sharedTreeOpen: open }),
    setThisTaskTreeOpen: (open) => set({ thisTaskTreeOpen: open }),
  }))
);
